import { ComponentType, useContext, useEffect, useRef } from "react";
import {
  WalkthroughContext,
  WalkthroughContextValue,
  WalkthroughSignalHandler,
} from "./walkthrough-context";

export function useWalkthrough(): WalkthroughContextValue | undefined {
  return useContext(WalkthroughContext);
}

/**
 * Subscribe to a walkthrough signal by name. The handler ref is kept stable
 * across renders so re-rendering with a new closure does not re-subscribe.
 */
export function useWalkthroughSignal(
  name: string,
  handler: WalkthroughSignalHandler,
): void {
  const ctx = useContext(WalkthroughContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => {
    if (!ctx) {
      return;
    }
    return ctx.subscribe(name, (payload) => handlerRef.current(payload));
  }, [ctx, name]);
}

/**
 * Read the active walkthrough's mock data source, typed by the caller.
 * Returns undefined when no walkthrough is active or its registry entry
 * has no mockSourceLoader.
 */
export function useWalkthroughMockSource<T>(): T | undefined {
  const ctx = useContext(WalkthroughContext);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return ctx?.mockSource as T | undefined;
}

/**
 * Read the active walkthrough's overlay component, typed by the caller.
 * Returns undefined when no walkthrough is active or its registry entry
 * has no overlayLoader.
 */
export function useWalkthroughOverlayComponent<P>():
  | ComponentType<P>
  | undefined {
  const ctx = useContext(WalkthroughContext);
  return ctx?.overlayComponent as ComponentType<P> | undefined;
}
